import { status } from '@/__mocks__/config.ts'
import {
  marketplaceOrdersPage1,
  marketplaceOrderDetailById,
  marketplacesPage1,
  marketplaceDetailById,
  marketplaceMutationResponse,
  marketplaceDeleteResponse,
  marketplaceProductsPage1,
  marketplaceProductDetailById,
  marketplaceProductMutationResponse,
  marketplaceProductDeleteResponse,
  marketplaceCategoriesResponse,
  marketplaceProductBranchOfficesCatalog,
  marketplaceProductModelsCatalog,
  marketplaceProductModelsCatalogByBrand,
  marketplaceProductBrandIds,
  inventoryMovementsPageByVariant,
  inventoryAdjustResponse,
  inventoryBulkUpdateResponse,
  inventorySummaryResponse,
  inventoryLowStockResponse,
  inventoryProductById,
  discountsList,
  discountMutationResponse,
  discountDeleteResponse
} from './data.ts'
import { wait } from '@/__mocks__/util/async.ts'

const marketplaceIds = [1, 2, 3, 4, 5, 6]

const marketplaceCrudRoutes = [
  {
    method: 'GET',
    path: '/marketplaces?limit=20',
    reply: async () => {
      await wait()
      return [status.ok, marketplacesPage1]
    }
  },
  // Catalog of marketplaces used by the selects of weppa users
  {
    method: 'GET',
    path: '/marketplaces?limit=100',
    reply: async () => {
      await wait()
      return [status.ok, marketplacesPage1]
    }
  },
  {
    method: 'POST',
    path: '/marketplaces',
    reply: async () => {
      await wait()
      return [status.ok, marketplaceMutationResponse]
    }
  },
  ...marketplaceIds.flatMap((id) => [
    {
      method: 'GET',
      path: `/marketplaces/${id}`,
      reply: async () => {
        await wait()
        return [status.ok, marketplaceDetailById[id]]
      }
    },
    {
      method: 'PUT',
      path: `/marketplaces/${id}`,
      reply: async () => {
        await wait()
        return [status.ok, marketplaceMutationResponse]
      }
    },
    {
      method: 'DELETE',
      path: `/marketplaces/${id}`,
      reply: async () => {
        await wait()
        return [status.ok, marketplaceDeleteResponse]
      }
    }
  ])
]

const productIds = [1, 2, 3, 4, 5, 6]
const productMarketplaceIds = [10, 11, 12]

const productCrudRoutes = [
  ...['/ally/products', '/products'].flatMap((base) =>
    ['?limit=20', '?limit=20&page=1'].map((query) => ({
      method: 'GET',
      path: `${base}${query}`,
      reply: async () => {
        await wait()
        return [status.ok, marketplaceProductsPage1]
      }
    }))
  ),
  // Product picker of the bulk stock update modal: no filters for regular
  // users, and the marketplace filter for weppa users
  ...[
    '/ally/products',
    ...marketplaceIds.map((id) => `/products?MarketplaceId=${id}`)
  ].map((path) => ({
    method: 'GET',
    path,
    reply: async () => {
      await wait()
      return [status.ok, marketplaceProductsPage1]
    }
  })),
  // Weppa users address a product through its marketplace; the rest use the ally scope
  ...[
    '/ally/products',
    ...productMarketplaceIds.map((id) => `/marketplaces/${id}/products`)
  ].map((path) => ({
    method: 'POST',
    path,
    reply: async () => {
      await wait()
      return [status.ok, marketplaceProductMutationResponse]
    }
  })),
  ...[
    '/ally/products',
    ...productMarketplaceIds.map((id) => `/marketplaces/${id}/products`)
  ].flatMap((base) =>
    productIds.flatMap((id) => [
      {
        method: 'GET',
        path: `${base}/${id}`,
        reply: async () => {
          await wait()
          return [status.ok, marketplaceProductDetailById[id]]
        }
      },
      {
        method: 'PUT',
        path: `${base}/${id}`,
        reply: async () => {
          await wait()
          return [status.ok, marketplaceProductMutationResponse]
        }
      },
      {
        method: 'PUT',
        path: `${base}/${id}/sponsored`,
        reply: async () => {
          await wait()
          return [status.ok, marketplaceProductMutationResponse]
        }
      },
      {
        method: 'DELETE',
        path: `${base}/${id}`,
        reply: async () => {
          await wait()
          return [status.ok, marketplaceProductDeleteResponse]
        }
      }
    ])
  ),
  {
    method: 'GET',
    path: '/categories',
    reply: async () => {
      await wait()
      return [status.ok, marketplaceCategoriesResponse]
    }
  },
  ...[1, 2, 3, 4, 5].map((allyId) => ({
    method: 'GET',
    path: `/branch-offices/catalog?commercialAllyId=${allyId}`,
    reply: async () => {
      await wait()
      return [status.ok, marketplaceProductBranchOfficesCatalog]
    }
  })),
  {
    method: 'GET',
    path: '/models/catalog',
    reply: async () => {
      await wait()
      return [status.ok, marketplaceProductModelsCatalog]
    }
  },
  ...marketplaceProductBrandIds.map((brandId) => ({
    method: 'GET',
    path: `/models/catalog?brand=${brandId}`,
    reply: async () => {
      await wait()
      return [status.ok, marketplaceProductModelsCatalogByBrand(brandId)]
    }
  }))
]

const inventoryProductIds = [...productIds, 103]
const inventoryVariantIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 103]

// Weppa users address the inventory through its marketplace; the rest use the ally scope
const inventoryBasePaths = [
  '/ally',
  ...marketplaceIds.map((id) => `/marketplaces/${id}`)
]

const inventoryCrudRoutes = [
  ...inventoryVariantIds.flatMap((id) => [
    {
      method: 'POST',
      path: `/variants/${id}/inventory/adjust`,
      reply: async () => {
        await wait()
        return [status.ok, inventoryAdjustResponse]
      }
    },
    {
      method: 'GET',
      path: `/variants/${id}/inventory/movements?limit=20`,
      reply: async () => {
        await wait()
        return [status.ok, inventoryMovementsPageByVariant[id]]
      }
    },
    {
      method: 'GET',
      path: `/variants/${id}/inventory/movements?limit=20&page=1`,
      reply: async () => {
        await wait()
        return [status.ok, inventoryMovementsPageByVariant[id]]
      }
    }
  ]),
  ...inventoryBasePaths.flatMap((base) => [
    {
      method: 'POST',
      path: `${base}/inventory/bulk-update`,
      reply: async () => {
        await wait()
        return [status.ok, inventoryBulkUpdateResponse]
      }
    },
    {
      method: 'GET',
      path: `${base}/inventory/summary`,
      reply: async () => {
        await wait()
        return [status.ok, inventorySummaryResponse]
      }
    },
    {
      method: 'GET',
      path: `${base}/inventory/summary?commercialAllyId=1`,
      reply: async () => {
        await wait()
        return [status.ok, inventorySummaryResponse]
      }
    },
    {
      method: 'GET',
      path: `${base}/inventory/low-stock?threshold=5`,
      reply: async () => {
        await wait()
        return [status.ok, inventoryLowStockResponse]
      }
    },
    {
      method: 'GET',
      path: `${base}/inventory/low-stock?threshold=5&commercialAllyId=1`,
      reply: async () => {
        await wait()
        return [status.ok, inventoryLowStockResponse]
      }
    },
    ...inventoryProductIds.map((id) => ({
      method: 'GET',
      path: `${base}/products/${id}/inventory`,
      reply: async () => {
        await wait()
        return [status.ok, inventoryProductById[id]]
      }
    }))
  ])
]

const discountCrudRoutes = [
  // Search: no filters, and the filters of the toolbar
  ...[
    '',
    '?IsActive=true',
    '?IsActive=false',
    '?DiscountType=PERCENTAGE',
    '?DiscountType=FIXED_AMOUNT',
    '?CurrentlyScheduled=true',
    '?CurrentlyScheduled=false',
    ...marketplaceIds.map((id) => `?MarketplaceId=${id}`)
  ].map((query) => ({
    method: 'GET',
    path: `/discounts${query}`,
    reply: async () => {
      await wait()
      return [status.ok, discountsList]
    }
  })),
  // Weppa users address a product through its marketplace; the rest use the ally scope
  ...marketplaceIds.flatMap((marketplaceId) =>
    productIds.map((productId) => ({
      method: 'POST',
      path: `/marketplaces/${marketplaceId}/products/${productId}/discounts`,
      reply: async () => {
        await wait()
        return [status.ok, discountMutationResponse]
      }
    }))
  ),
  ...productIds.map((productId) => ({
    method: 'POST',
    path: `/ally/products/${productId}/discounts`,
    reply: async () => {
      await wait()
      return [status.ok, discountMutationResponse]
    }
  })),
  ...discountsList.results.flatMap(({ discountId }) => [
    {
      method: 'PUT',
      path: `/discounts/${discountId}`,
      reply: async () => {
        await wait()
        return [status.ok, discountMutationResponse]
      }
    },
    {
      method: 'DELETE',
      path: `/discounts/${discountId}`,
      reply: async () => {
        await wait()
        return [status.ok, discountDeleteResponse]
      }
    }
  ])
]

export default [
  ...marketplaceCrudRoutes,
  ...productCrudRoutes,
  ...inventoryCrudRoutes,
  ...discountCrudRoutes,
  {
    method: 'GET',
    path: '/orders?limit=20',
    reply: async () => {
      await wait()
      return [status.ok, marketplaceOrdersPage1]
    }
  },
  ...marketplaceIds.flatMap((id) => [
    {
      method: 'GET',
      path: `/orders/${id}`,
      reply: async () => {
        await wait()
        return [status.ok, marketplaceOrderDetailById[id]]
      }
    },
    {
      method: 'PATCH',
      path: `/orders/${id}/status`,
      reply: async () => {
        await wait()
        return [status.ok, { data: {} }]
      }
    }
  ])
]
